/**
 * ActivityTrendsCard Component
 * Displays per-business-day activity metrics (Current vs 6-Month Average)
 * Created: 2025-11-17
 */

import React, { useMemo } from 'react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import {
  calculate6MonthRollingAverage,
  calculateCurrentMonthRates,
  compareMetrics
} from '../utils/trendingCalculations';

/**
 * Get last 5 business days (Mon-Fri only) as YYYY-MM-DD strings
 * Returns in chronological order (oldest first) for left-to-right sparkline trend
 */
function getLast5BusinessDays() {
  const dates = [];
  const today = new Date();
  let current = new Date(today);

  while (dates.length < 5) {
    const dayOfWeek = current.getDay();
    // Only include Mon-Fri (1-5), skip Sat (6) and Sun (0)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      dates.unshift(current.toISOString().split('T')[0]); // Add to front for chronological order
    }
    current.setDate(current.getDate() - 1);
  }

  return dates;
}

export default function ActivityTrendsCard({ data, selectedRep, month, reps, developerMode }) {
  // Calculate business days elapsed in current month
  const businessDaysElapsed = useMemo(() => {
    const today = new Date();
    const year = today.getFullYear();
    const currentMonth = today.getMonth();

    let businessDays = 0;
    for (let day = 1; day <= today.getDate(); day++) {
      const date = new Date(year, currentMonth, day);
      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        businessDays++;
      }
    }
    return businessDays;
  }, []);

  // Current month per-day rates
  const currentRates = useMemo(() => {
    return calculateCurrentMonthRates(data, selectedRep, month, businessDaysElapsed);
  }, [data, selectedRep, month, businessDaysElapsed]);

  // 6-month rolling averages
  const historicalAverages = useMemo(() => {
    const metrics = [
      { key: 'calls', tab: 'Dials_TalkTime', field: null },
      { key: 'sms', tab: 'SMS', field: 'Count' },
      { key: 'apps', tab: 'Apps_In', field: null },
      { key: 'approved', tab: 'Approved', field: null },
      { key: 'funded', tab: 'Funded', field: null },
      { key: 'revenue', tab: 'Revenue', field: 'Revenue' }
    ];

    const averages = {};

    metrics.forEach(({ key, tab, field }) => {
      const result = calculate6MonthRollingAverage(data, selectedRep, month, { tab, field });
      averages[key] = result.avgPerDay;
    });

    return averages;
  }, [data, selectedRep, month]);

  // Compare current vs historical
  const comparisons = useMemo(() => {
    return {
      calls: compareMetrics(currentRates.callsPerDay, historicalAverages.calls),
      sms: compareMetrics(currentRates.smsPerDay, historicalAverages.sms),
      apps: compareMetrics(currentRates.appsPerDay, historicalAverages.apps),
      approved: compareMetrics(currentRates.approvedPerDay, historicalAverages.approved),
      funded: compareMetrics(currentRates.fundedPerDay, historicalAverages.funded),
      revenue: compareMetrics(currentRates.revenuePerDay, historicalAverages.revenue)
    };
  }, [currentRates, historicalAverages]);

  // Calculate REAL daily trends for sparklines (last 5 business days)
  const dailyTrends = useMemo(() => {
    if (!data || !data.tabs) {
      return { calls: [], sms: [], apps: [], approved: [], funded: [], revenue: [] };
    }

    const last5Days = getLast5BusinessDays();
    const isAllReps = selectedRep === 'All Reps' || selectedRep === 'Team';

    // Helper to get daily total for a metric
    const getDailyTotals = (tabName, aggregationType, field = null) => {
      const tabData = data.tabs[tabName]?.data || [];

      return last5Days.map(dateStr => {
        const dayRecords = tabData.filter(r => {
          const matchesDate = r.Date?.split('T')[0] === dateStr;
          const matchesRep = isAllReps || r.Rep === selectedRep;
          return matchesDate && matchesRep;
        });

        if (aggregationType === 'count') {
          return dayRecords.length;
        } else if (aggregationType === 'sum' && field) {
          return dayRecords.reduce((sum, r) => sum + (parseFloat(r[field]) || 0), 0);
        }
        return 0;
      });
    };

    return {
      calls: getDailyTotals('Dials_TalkTime', 'count'),
      sms: getDailyTotals('SMS', 'sum', 'Count'),
      apps: getDailyTotals('Apps_In', 'count'),
      approved: getDailyTotals('Approved', 'count'),
      funded: getDailyTotals('Funded', 'count'),
      revenue: getDailyTotals('Revenue', 'sum', 'Revenue')
    };
  }, [data, selectedRep]);

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      marginBottom: '20px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      position: 'relative'
    }}>
      {/* Developer Mode: Section Label */}
      {developerMode && (
        <div className="dev-section-label">S-3</div>
      )}
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <span style={{ fontSize: '20px', marginRight: '8px' }}>📈</span>
        <h3 style={{
          margin: 0,
          fontSize: '18px',
          fontWeight: 700,
          color: '#1e293b'
        }}>
          Activity Trends
        </h3>
        <span style={{
          marginLeft: 'auto',
          fontSize: '13px',
          color: '#64748b',
          fontWeight: 500
        }}>
          Per Business Day: Current vs 6-Month Avg
        </span>
      </div>

      {/* Activity Metrics Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <ActivityMetric
          label="Calls/day"
          comparison={comparisons.calls}
          format="number"
          trendData={dailyTrends.calls}
          developerMode={developerMode}
          chartNumber={1}
        />

        <ActivityMetric
          label="SMS/day"
          comparison={comparisons.sms}
          format="number"
          trendData={dailyTrends.sms}
          developerMode={developerMode}
          chartNumber={2}
        />

        <ActivityMetric
          label="Apps/day"
          comparison={comparisons.apps}
          format="decimal"
          trendData={dailyTrends.apps}
          developerMode={developerMode}
          chartNumber={3}
        />

        <ActivityMetric
          label="Approved/day"
          comparison={comparisons.approved}
          format="decimal"
          trendData={dailyTrends.approved}
          developerMode={developerMode}
          chartNumber={4}
        />

        <ActivityMetric
          label="Funded/day"
          comparison={comparisons.funded}
          format="decimal"
          trendData={dailyTrends.funded}
          developerMode={developerMode}
          chartNumber={5}
        />

        <ActivityMetric
          label="Revenue/day"
          comparison={comparisons.revenue}
          format="currency"
          trendData={dailyTrends.revenue}
          developerMode={developerMode}
          chartNumber={6}
        />
      </div>

      {/* Baseline Info */}
      <div style={{
        fontSize: '12px',
        color: '#64748b',
        textAlign: 'center',
        fontStyle: 'italic',
        padding: '8px',
        backgroundColor: '#f8fafc',
        borderRadius: '4px'
      }}>
        Baseline: 6-month rolling average
      </div>
    </div>
  );
}

/**
 * Activity Metric Component
 * @param {string} label - Metric label
 * @param {object} comparison - Comparison data from compareMetrics()
 * @param {string} format - Display format ('number', 'decimal', 'currency')
 * @param {number[]} trendData - Array of 5 real daily values for sparkline
 */
function ActivityMetric({ label, comparison, format, trendData, developerMode, chartNumber }) {
  const { value, historicalAvg, variance, variancePercent, trend, indicator } = comparison;

  // Format values based on type
  const formatValue = (val) => {
    if (format === 'currency') {
      return `$${(val / 1000).toFixed(1)}K`;
    } else if (format === 'decimal') {
      return val.toFixed(1);
    } else {
      return Math.round(val);
    }
  };

  // Determine color based on trend
  const getColor = () => {
    if (label === 'Revenue/day' || label === 'Apps/day' || label === 'Funded/day' || label === 'Approved/day') {
      // For these metrics, up is good
      if (trend === 'up') return '#10b981'; // Green
      if (trend === 'down') return '#ef4444'; // Red
    } else {
      // For Calls and SMS, up could be good or neutral
      if (trend === 'up') return '#3b82f6'; // Blue (neutral/informational)
      if (trend === 'down') return '#f59e0b'; // Orange (warning)
    }
    return '#64748b'; // Gray (stable)
  };

  const color = getColor();

  // Convert real daily trend data to sparkline format
  // trendData = [day1, day2, day3, day4, day5] - actual values from last 5 business days
  const sparklineData = useMemo(() => {
    if (!trendData || trendData.length === 0) {
      // Fallback only if no data at all - show flat line at current value
      return [{ value: value || 0 }, { value: value || 0 }];
    }
    return trendData.map(v => ({ value: v }));
  }, [trendData, value]);

  return (
    <div style={{
      padding: '12px',
      backgroundColor: '#f8fafc',
      borderRadius: '6px',
      border: '1px solid #e2e8f0',
      position: 'relative'
    }}>
      {/* Developer Mode: Chart Number Label */}
      {developerMode && (
        <div className="dev-chart-label">C-{chartNumber}</div>
      )}
      {/* Header with Label and Sparkline */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '8px'
      }}>
        {/* Label */}
        <div style={{
          fontSize: '12px',
          fontWeight: 600,
          color: '#475569',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          {label}
        </div>

        {/* Sparkline */}
        <div style={{ width: '80px', height: '40px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparklineData}>
              <Line
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Current Value */}
      <div style={{
        display: 'flex',
        alignItems: 'baseline',
        marginBottom: '6px'
      }}>
        <span style={{
          fontSize: '24px',
          fontWeight: 700,
          color: '#1e293b',
          marginRight: '8px'
        }}>
          {formatValue(value)}
        </span>
        <span style={{
          fontSize: '20px',
          color,
          fontWeight: 600
        }}>
          {indicator}
        </span>
      </div>

      {/* Variance */}
      <div style={{
        fontSize: '13px',
        color: '#64748b'
      }}>
        <span style={{
          fontWeight: 600,
          color
        }}>
          {variance > 0 ? '+' : ''}{formatValue(variance)}
        </span>
        {' '}vs{' '}
        <span style={{ fontWeight: 500 }}>
          {formatValue(historicalAvg)}/day avg
        </span>
      </div>

      {/* Percentage Change */}
      <div style={{
        fontSize: '12px',
        fontWeight: 600,
        color,
        marginTop: '4px'
      }}>
        {variancePercent > 0 ? '+' : ''}{variancePercent.toFixed(1)}%
      </div>
    </div>
  );
}
